import type { ReactNode } from 'react';
import type { CustomFieldValue, CustomFieldView } from '@nest-recruit/shared';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input, Textarea } from '../../../components/Input';
import { MultiSelect, Select } from '../../../components/Select';

const YES = 'yes';
const NO = 'no';
const YES_NO_OPTIONS = [
  { value: YES, label: 'Yes' },
  { value: NO, label: 'No' },
];

interface CustomFieldControlProps {
  readonly field: CustomFieldView;
  readonly value: CustomFieldValue | null;
  readonly onChange: (value: CustomFieldValue | null) => void;
  readonly error?: string;
  readonly disabled?: boolean;
}

/** ADMIN-13: one field, drawn with the control its type calls for — never a stock browser control. */
export function CustomFieldControl({ field, value, onChange, error, disabled = false }: CustomFieldControlProps): ReactNode {
  return (
    <Field label={field.label} hint={field.helpText ?? undefined} error={error} isRequired={field.isRequired}>
      {({ inputId, describedBy, isInvalid }) => {
        switch (field.fieldType) {
          case 'short_text':
            return <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={disabled} maxLength={field.maxLength ?? undefined} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} />;
          case 'long_text':
            return <Textarea id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} disabled={disabled} rows={3} maxLength={field.maxLength ?? undefined} value={typeof value === 'string' ? value : ''} onChange={(event) => onChange(event.target.value)} />;
          case 'number':
            return (
              <Input
                id={inputId}
                aria-describedby={describedBy}
                isInvalid={isInvalid}
                disabled={disabled}
                type="number"
                inputMode="decimal"
                min={field.minNumber ?? undefined}
                max={field.maxNumber ?? undefined}
                value={typeof value === 'number' ? String(value) : ''}
                onChange={(event) => onChange(event.target.value === '' ? null : Number(event.target.value))}
              />
            );
          case 'date':
            return <DatePicker id={inputId} describedBy={describedBy} isInvalid={isInvalid} disabled={disabled} label={field.label} value={typeof value === 'string' ? value : ''} onChange={(next) => onChange(next === '' ? null : next)} />;
          case 'yes_no':
            return <Select id={inputId} describedBy={describedBy} isInvalid={isInvalid} disabled={disabled} label={field.label} placeholder="—" options={YES_NO_OPTIONS} value={yesNoOf(value)} onChange={(next) => onChange(next === YES)} />;
          case 'one_choice':
            return <Select id={inputId} describedBy={describedBy} isInvalid={isInvalid} disabled={disabled} label={field.label} options={field.options.map((option) => ({ value: option, label: option }))} value={typeof value === 'string' ? value : null} onChange={onChange} />;
          case 'many_choices':
            return <MultiSelect id={inputId} describedBy={describedBy} isInvalid={isInvalid} disabled={disabled} label={field.label} options={field.options.map((option) => ({ value: option, label: option }))} value={Array.isArray(value) ? value : []} onChange={onChange} />;
        }
      }}
    </Field>
  );
}

function yesNoOf(value: CustomFieldValue | null): string | null {
  if (value === true) return YES;
  if (value === false) return NO;
  return null;
}

/** A value as the API takes it: plain arrays, trimmed text, and an empty text as "no value". */
export function wireValue(value: CustomFieldValue | null): string | number | boolean | string[] | null {
  if (value === null) return null;
  if (typeof value === 'string') return value.trim() === '' ? null : value.trim();
  if (typeof value === 'object') return [...value];
  return value;
}
